/**
 * One filter bar, five reports.
 *
 * The product being replaced put a different toolbar on every report — some had
 * a From/To pair, one had a sport dropdown that filtered the table but not the
 * totals above it, and none of them survived a page reload or a shared link. So
 * two operators looking at "the same report" routinely had different numbers on
 * screen and no way to discover why.
 *
 * The rules that fix it, and which every dimension here obeys:
 *
 *  1. **Every dimension lives in the URL.** The URL is the report. A link
 *     pasted into a colleague's tab reproduces the exact view, scope included.
 *  2. **Defaults are never written.** A shared link contains only what was
 *     actually changed, so reading it tells you what is unusual about it.
 *  3. **Unknown values are dropped, never thrown on.** A hand-edited or stale
 *     URL degrades to the default rather than 404-ing a report.
 *  4. **Every filter change resets `page`.** Narrowing while parked on page 4
 *     strands the operator on an empty list — the Transfers page learned this
 *     the hard way, and here it is structural rather than remembered.
 *  5. **A report only offers a filter its query can honour.** Every omission is
 *     stated out loud inside the Filters panel instead of shipping a chip that
 *     quietly does nothing, which is the precise defect this console exists to
 *     remove.
 *
 * `scope` is the one dimension that persists across report navigation, because
 * it answers *whose book am I reading* — the same question on all five screens.
 * Sport, game and member answer *which slice*, and a slice is report-specific:
 * carrying a Tennis filter from Turnover onto Commission would produce a
 * mystifying empty table.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom'
import {
  Banner,
  Button,
  Drawer,
  FilterBar,
  FilterChip,
  Popover,
  SearchInput,
  Segmented,
  StateBlock,
  DATE_RANGE_PRESETS,
  rangeSpanDays,
  resolveRangePreset,
  type ActiveFilter,
  type DateRangePresetId,
  type IconName,
} from '@w7/ui'
import { GAME_FILTERS, SPORT_FILTERS, useAccount, useSession } from '@/app/queries'
import { useDateRange } from '@/app/range'
import {
  ROLES,
  ROLE_LABEL,
  isAgent,
  roleDepth,
  type Account,
  type DateRange,
  type GameBucket,
  type Role,
  type Sport,
} from '@/data/types'
import { RangePicker, presetOf, rangeLabel } from './RangePicker'
import { HierarchyScopePicker } from './HierarchyScopePicker'
import { MemberPicker, type MemberSource } from './MemberPicker'
import './reports.css'

/* ------------------------------------------------------------- vocabulary -- */

export type Dimension = 'range' | 'scope' | 'sport' | 'game' | 'member' | 'role' | 'dir' | 'q'

/** The dimensions that live behind the "Filters" button rather than inline. */
const COLLAPSED: Dimension[] = ['sport', 'game', 'role', 'dir']

/** Which URL params each dimension owns, so `clear` can be exhaustive. */
const PARAM_KEYS: Record<Dimension, string[]> = {
  range: ['preset', 'from', 'to'],
  scope: ['scope'],
  sport: ['sport'],
  game: ['game'],
  member: ['member'],
  role: ['role'],
  dir: ['dir'],
  q: ['q'],
}

/** Counterparty tiers offered on Credit transfers — every role, members included. */
const ROLE_OPTIONS: Role[] = [...ROLES]

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/
/** The fixture and the analytics endpoints both degrade badly past a year. */
const MAX_SPAN_DAYS = 366

/**
 * Where a non-self scope is remembered between reports.
 *
 * `sessionStorage`, not `localStorage`: a scope is a thing you are doing right
 * now, and finding yesterday's Master still selected on a fresh morning would
 * be a genuinely dangerous surprise on a money screen. Every access is wrapped
 * — private-mode Safari throws on the first read.
 */
const SCOPE_STORE = 'w7.console.reportScope'

const readStoredScope = (): string | null => {
  try {
    return window.sessionStorage.getItem(SCOPE_STORE)
  } catch {
    return null
  }
}

const writeStoredScope = (id: string | null) => {
  try {
    if (id) window.sessionStorage.setItem(SCOPE_STORE, id)
    else window.sessionStorage.removeItem(SCOPE_STORE)
  } catch {
    /* storage unavailable — scope simply does not persist, which is safe */
  }
}

/* ------------------------------------------------------------------ types -- */

export interface ReportRange extends DateRange {
  label: string
  days: number
}

export interface FilterValues {
  preset: DateRangePresetId
  range: ReportRange
  /** True when the range came from the URL rather than the shell's default. */
  rangePinned: boolean
  /** The account this report is being read as, or null for your own seat. */
  scope: string | null
  sports: Sport[]
  games: GameBucket[]
  members: string[]
  roles: Role[]
  dir: 'give' | 'take' | null
  q: string
  page: number
}

export interface FilterPatch {
  range?: { preset: DateRangePresetId } & DateRange
  scope?: string | null
  sports?: Sport[]
  games?: GameBucket[]
  members?: string[]
  roles?: Role[]
  dir?: 'give' | 'take' | null
  q?: string
}

export interface ReportFilterState {
  values: FilterValues
  dimensions: readonly Dimension[]
  has: (dimension: Dimension) => boolean
  set: (patch: FilterPatch) => void
  setPage: (page: number) => void
  clearDimension: (dimension: Dimension) => void
  clearAll: () => void
  /** Anything at all narrowing or re-seating the view. */
  isFiltered: boolean
  /** One sentence naming the narrowest active filter, for a filtered-empty state. */
  narrowest: string | null
  /** The scoped account, once resolved. `null` while you are on your own seat. */
  scopeAccount: Account | null
  /** The `scope` param points somewhere that is not beneath you. */
  scopeInvalid: boolean
  scopeLoading: boolean
  /** The signed-in account — the root of the scope tree and the default seat. */
  self: Account | undefined
}

/* ------------------------------------------------------------ url plumbing -- */

/**
 * Write a set of params, deleting anything set to null/undefined/''.
 *
 * `replace: true` throughout: a filter change is a refinement of where you
 * already are, not a new place. Fifteen back-presses to escape a report is the
 * behaviour every URL-backed filter bar gets wrong first.
 */
export function patchParams(
  params: URLSearchParams,
  setParams: SetURLSearchParams,
  next: Record<string, string | string[] | null | undefined>,
) {
  const copy = new URLSearchParams(params)
  for (const [key, value] of Object.entries(next)) {
    copy.delete(key)
    if (value === null || value === undefined || value === '') continue
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item !== '') copy.append(key, item)
    }
  }
  setParams(copy, { replace: true })
}

const pickAll = <T extends string>(params: URLSearchParams, key: string, allowed: readonly T[]): T[] =>
  params.getAll(key).filter((v): v is T => (allowed as readonly string[]).includes(v))

/* -------------------------------------------------------------------- hook -- */

/**
 * @param dimensions Must be a stable reference — declare it at module scope in
 *   the page. An inline array would rebuild every parse on every render.
 */
export function useReportFilters(dimensions: readonly Dimension[]): ReportFilterState {
  const [params, setParams] = useSearchParams()
  const { range: shellRange } = useDateRange()
  const { data: session } = useSession()

  const dimKey = dimensions.join(',')
  const has = useCallback(
    (dimension: Dimension) => dimensions.includes(dimension),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dimKey],
  )

  const values = useMemo<FilterValues>(() => {
    /* ---- range ---- */
    let from = shellRange.from
    let to = shellRange.to
    let preset: DateRangePresetId = 'custom'
    let rangePinned = false

    if (has('range')) {
      const rawPreset = params.get('preset')
      const known = DATE_RANGE_PRESETS.some((p) => p.id === rawPreset)
        ? (rawPreset as DateRangePresetId)
        : null
      const rawFrom = params.get('from')
      const rawTo = params.get('to')
      const datesOk =
        rawFrom !== null &&
        rawTo !== null &&
        DAY_RE.test(rawFrom) &&
        DAY_RE.test(rawTo) &&
        rawFrom <= rawTo &&
        (rangeSpanDays({ from: rawFrom, to: rawTo }) ?? 0) <= MAX_SPAN_DAYS

      if (datesOk) {
        // Explicit dates win over the preset name. A link shared on Friday must
        // still show the same week when it is opened on Monday.
        from = rawFrom
        to = rawTo
        rangePinned = true
      } else if (known && known !== 'custom') {
        const resolved = resolveRangePreset(known)
        if (resolved) {
          from = resolved.from
          to = resolved.to
          rangePinned = true
        }
      }
      preset = known ?? presetOf({ from, to })
    } else {
      preset = presetOf({ from, to })
    }

    return {
      preset,
      range: { from, to, label: rangeLabel({ from, to }), days: rangeSpanDays({ from, to }) ?? 1 },
      rangePinned,
      scope: has('scope') ? (params.get('scope') || null) : null,
      sports: has('sport') ? pickAll<Sport>(params, 'sport', SPORT_FILTERS) : [],
      games: has('game') ? pickAll<GameBucket>(params, 'game', GAME_FILTERS) : [],
      // Member ids have no closed vocabulary to check against here; an id that
      // is not beneath you simply matches nothing, which is the same answer the
      // API gives for an out-of-subtree read.
      members: has('member') ? params.getAll('member').filter(Boolean) : [],
      roles: has('role') ? pickAll<Role>(params, 'role', ROLE_OPTIONS) : [],
      dir: has('dir')
        ? params.get('dir') === 'give'
          ? 'give'
          : params.get('dir') === 'take'
            ? 'take'
            : null
        : null,
      q: has('q') ? (params.get('q') ?? '') : '',
      page: Math.max(1, Math.trunc(Number(params.get('page')) || 1)),
    }
  }, [has, params, shellRange.from, shellRange.to])

  /* ---- scope resolution ---- */

  const scopeQuery = useAccount(values.scope ?? undefined)
  const scopeAccount = values.scope ? (scopeQuery.data ?? null) : null
  const scopeInvalid = Boolean(values.scope) && scopeQuery.isSuccess && !scopeQuery.data
  const scopeLoading = Boolean(values.scope) && scopeQuery.isLoading

  /* Adopt a remembered scope when the URL carries none, then WRITE it into the
     URL. The param is the record of what the page is showing; leaving the scope
     implicit in storage would make a screenshot ambiguous about whose money it
     is, which is the one thing a scoped report must never be. */
  const adopted = useRef(false)
  useEffect(() => {
    if (adopted.current || !has('scope')) return
    adopted.current = true
    if (params.get('scope')) return
    const stored = readStoredScope()
    if (stored) patchParams(params, setParams, { scope: stored })
    // Mount only: after this, scope changes because someone picked one.
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* A scope that is not beneath you must not follow you to the next report. The
     param stays so the URL still explains the state block on screen; only the
     memory of it is dropped. */
  useEffect(() => {
    if (scopeInvalid) writeStoredScope(null)
  }, [scopeInvalid])

  /* ---- writers ---- */

  const set = useCallback(
    (patch: FilterPatch) => {
      // Every filter change returns to page 1.
      const next: Record<string, string | string[] | null> = { page: null }
      if (patch.range) {
        next.preset = patch.range.preset
        next.from = patch.range.from
        next.to = patch.range.to
      }
      if ('scope' in patch) {
        next.scope = patch.scope ?? null
        writeStoredScope(patch.scope ?? null)
      }
      if (patch.sports) next.sport = patch.sports
      if (patch.games) next.game = patch.games
      if (patch.members) next.member = patch.members
      if (patch.roles) next.role = patch.roles
      if ('dir' in patch) next.dir = patch.dir ?? null
      if ('q' in patch) next.q = patch.q?.trim() ? patch.q : null
      patchParams(params, setParams, next)
    },
    [params, setParams],
  )

  const setPage = useCallback(
    (page: number) => patchParams(params, setParams, { page: page > 1 ? String(page) : null }),
    [params, setParams],
  )

  const clearDimension = useCallback(
    (dimension: Dimension) => {
      const next: Record<string, null> = { page: null }
      for (const key of PARAM_KEYS[dimension]) next[key] = null
      if (dimension === 'scope') writeStoredScope(null)
      patchParams(params, setParams, next)
    },
    [params, setParams],
  )

  const clearAll = useCallback(() => {
    const next: Record<string, null> = { page: null }
    for (const dimension of dimensions) for (const key of PARAM_KEYS[dimension]) next[key] = null
    writeStoredScope(null)
    patchParams(params, setParams, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimKey, params, setParams])

  const isFiltered =
    values.rangePinned ||
    values.scope !== null ||
    values.sports.length > 0 ||
    values.games.length > 0 ||
    values.members.length > 0 ||
    values.roles.length > 0 ||
    values.dir !== null ||
    values.q.trim().length > 0

  /* Named narrowest-first, because that is the filter most likely to be the one
     emptying the table, and a filtered-empty state that names the wrong filter
     is worse than one that names none. */
  const narrowest =
    values.q.trim().length > 0
      ? `The text filter is set to “${values.q.trim()}”.`
      : values.members.length > 0
        ? `The list is limited to ${values.members.length} selected ${values.members.length === 1 ? 'account' : 'accounts'}.`
        : values.games.length > 0
          ? `Game is limited to ${values.games.join(', ')}.`
          : values.sports.length > 0
            ? `Sport is limited to ${values.sports.join(', ')}.`
            : values.roles.length > 0
              ? `Tier is limited to ${values.roles.map((r) => ROLE_LABEL[r]).join(', ')}.`
              : values.dir !== null
                ? `Direction is limited to credit ${values.dir === 'give' ? 'given' : 'taken'}.`
                : values.scope !== null
                  ? `This report is scoped to ${scopeAccount?.username ?? 'another account'}.`
                  : values.rangePinned
                    ? `The range is ${values.range.label}.`
                    : null

  return {
    values,
    dimensions,
    has,
    set,
    setPage,
    clearDimension,
    clearAll,
    isFiltered,
    narrowest,
    scopeAccount,
    scopeInvalid,
    scopeLoading,
    self: session?.account,
  }
}

/* --------------------------------------------------------------- the bar --- */

export interface ReportFiltersProps {
  state: ReportFilterState
  /** Which population the member picker offers. */
  memberSource?: MemberSource
  memberLabel?: string
  memberAllLabel?: string
  textLabel?: string
  textPlaceholder?: string
  /** Heading for the role chips — "Counterparty tier", "Agent tier". */
  roleLabel?: string
  /** One line under the role chips saying what they narrow. */
  roleNote?: ReactNode
  /** Stated inside the panel: a dimension this report deliberately does not offer. */
  omission?: ReactNode
  actions?: ReactNode
  status?: ReactNode
}

export function ReportFilters({
  state,
  memberSource = 'settled',
  memberLabel = 'Members',
  memberAllLabel = 'All members',
  textLabel = 'Filter rows',
  textPlaceholder = 'Filter rows',
  roleLabel = 'Tier',
  roleNote,
  omission,
  actions,
  status,
}: ReportFiltersProps) {
  const { values, has, set } = state
  const collapsed = COLLAPSED.filter(has)

  /* Chip labels are plain strings on purpose: the sr-only live line below is
     built from the same array, and a ReactNode there would announce as [object
     Object] or as nothing at all. */
  const chips: ActiveFilter[] = []
  if (has('range') && values.rangePinned) {
    chips.push({
      id: 'range',
      label: values.range.label,
      removeLabel: 'Remove the date range filter and go back to the default period',
      onRemove: () => state.clearDimension('range'),
    })
  }
  if (has('scope') && values.scope) {
    chips.push({
      id: 'scope',
      label: `Viewing as ${state.scopeAccount?.username ?? values.scope}`,
      removeLabel: 'Stop viewing this report as another account',
      onRemove: () => state.clearDimension('scope'),
    })
  }
  if (has('sport') && values.sports.length > 0) {
    chips.push(listChip('sport', 'Sport', 'Sports', values.sports, () => state.clearDimension('sport')))
  }
  if (has('game') && values.games.length > 0) {
    chips.push(listChip('game', 'Game', 'Games', values.games, () => state.clearDimension('game')))
  }
  if (has('role') && values.roles.length > 0) {
    chips.push(
      listChip(
        'role',
        roleLabel,
        `${roleLabel}s`,
        values.roles.map((r) => ROLE_LABEL[r]),
        () => state.clearDimension('role'),
      ),
    )
  }
  if (has('dir') && values.dir) {
    chips.push({
      id: 'dir',
      label: `Direction: ${values.dir === 'give' ? 'Given' : 'Taken'}`,
      removeLabel: 'Remove the direction filter',
      onRemove: () => state.clearDimension('dir'),
    })
  }
  if (has('member') && values.members.length > 0) {
    chips.push({
      id: 'member',
      label: `${memberLabel}: ${values.members.length} selected`,
      removeLabel: `Remove the ${memberLabel.toLowerCase()} filter`,
      onRemove: () => state.clearDimension('member'),
    })
  }
  if (has('q') && values.q.trim()) {
    chips.push({
      id: 'q',
      label: `Search: “${values.q.trim()}”`,
      removeLabel: 'Remove the text filter',
      onRemove: () => state.clearDimension('q'),
    })
  }

  /* Announced once per change rather than per keystroke: the text box commits
     on a debounce, so this only fires when something has actually landed. */
  const announced =
    chips.length === 0
      ? 'No filters are active.'
      : `${chips.length} filter${chips.length === 1 ? '' : 's'} active: ${chips
          .map((chip) => (typeof chip.label === 'string' ? chip.label : ''))
          .filter(Boolean)
          .join(', ')}.`

  return (
    <div className="w7-reportbar">
      <FilterBar
        label="Report filters"
        active={chips}
        onClearAll={state.isFiltered ? state.clearAll : undefined}
        /* `margin-left: auto` and not the bar's own spacer: once the controls
           wrap to a second line the spacer has already been consumed by the
           first, and provenance + export end up left-aligned under the range
           picker looking like two more filters. */
        actions={
          actions ? (
            <span className="w7-row w7-row-wrap" style={{ marginLeft: 'auto' }}>
              {actions}
            </span>
          ) : undefined
        }
        status={status ?? announced}
      >
        {has('range') ? (
          <RangePicker preset={values.preset} value={values.range} onChange={(next) => set({ range: next })} />
        ) : null}

        {has('scope') ? (
          <HierarchyScopePicker
            value={values.scope}
            onChange={(scope) => set({ scope })}
            self={state.self}
            scoped={state.scopeAccount}
          />
        ) : null}

        {has('member') ? (
          <MemberPicker
            selected={values.members}
            onChange={(members) => set({ members })}
            source={memberSource}
            range={values.range}
            rootId={values.scope}
            label={memberLabel}
            allLabel={memberAllLabel}
          />
        ) : null}

        {collapsed.length > 0 ? (
          <CollapsedFilters
            state={state}
            collapsed={collapsed}
            roleLabel={roleLabel}
            roleNote={roleNote}
            omission={omission}
          />
        ) : null}

        {has('q') ? (
          <FilterText
            value={values.q}
            onCommit={(q) => set({ q })}
            label={textLabel}
            placeholder={textPlaceholder}
          />
        ) : null}
      </FilterBar>
    </div>
  )
}

/** `Sport: Cricket` · `Sports: Cricket, Tennis` · `Sports: Cricket, Tennis +2`. */
function listChip(
  id: string,
  singular: string,
  plural: string,
  items: string[],
  onRemove: () => void,
): ActiveFilter {
  const head = items.slice(0, 2).join(', ')
  const rest = items.length - 2
  return {
    id,
    label: `${items.length === 1 ? singular : plural}: ${head}${rest > 0 ? ` +${rest}` : ''}`,
    // The × clears the whole dimension; individual values stay removable inside
    // the panel. A chip that removed only its first named value would be a trap.
    removeLabel: `Remove the ${plural.toLowerCase()} filter`,
    onRemove,
  }
}

/* ------------------------------------------------------- collapsed panel --- */

function CollapsedFilters({
  state,
  collapsed,
  roleLabel,
  roleNote,
  omission,
}: {
  state: ReportFilterState
  collapsed: Dimension[]
  roleLabel: string
  roleNote?: ReactNode
  omission?: ReactNode
}) {
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const narrow = useNarrow()
  const { values, set } = state

  /* Counts DIMENSIONS, not values: three sports is one thing narrowing your
     numbers, and a badge reading 3 for it would suggest three. */
  const active = collapsed.filter((d) =>
    d === 'sport'
      ? values.sports.length > 0
      : d === 'game'
        ? values.games.length > 0
        : d === 'role'
          ? values.roles.length > 0
          : values.dir !== null,
  ).length

  /* An explicit member list beats a tier rule, so the two must not both be on:
     the tier chips would appear to do nothing. Disabled WITH a reason, rather
     than hidden, because hiding them would look like a rendering fault. */
  const rolesBlocked = state.has('member') && values.members.length > 0

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  const body = (
    <>
      {collapsed.includes('sport') ? (
        <div className="w7-filtergroup">
          <span className="w7-filtergroup-head">Sport</span>
          <div className="w7-chiprow">
            {SPORT_FILTERS.map((sport) => (
              <FilterChip
                key={sport}
                active={values.sports.includes(sport)}
                onClick={() => set({ sports: toggle(values.sports, sport) })}
              >
                {sport}
              </FilterChip>
            ))}
          </div>
        </div>
      ) : null}

      {collapsed.includes('game') ? (
        <div className="w7-filtergroup">
          <span className="w7-filtergroup-head">Game</span>
          <div className="w7-chiprow">
            {GAME_FILTERS.map((game) => (
              <FilterChip
                key={game}
                active={values.games.includes(game)}
                onClick={() => set({ games: toggle(values.games, game) })}
              >
                {game}
              </FilterChip>
            ))}
          </div>
        </div>
      ) : null}

      {collapsed.includes('role') ? (
        <div className="w7-filtergroup">
          <span className="w7-filtergroup-head">{roleLabel}</span>
          <div className="w7-chiprow">
            {ROLE_OPTIONS.map((role) => (
              <FilterChip
                key={role}
                active={values.roles.includes(role)}
                onClick={() => {
                  if (rolesBlocked) return
                  set({
                    roles: toggle(values.roles, role).sort((a, b) => roleDepth(a) - roleDepth(b)),
                  })
                }}
              >
                {ROLE_LABEL[role]}
              </FilterChip>
            ))}
          </div>
          {rolesBlocked ? (
            <p className="w7-filtergroup-note">Clear the member selection to filter by tier.</p>
          ) : roleNote ? (
            <p className="w7-filtergroup-note">{roleNote}</p>
          ) : null}
        </div>
      ) : null}

      {collapsed.includes('dir') ? (
        <div className="w7-filtergroup">
          <span className="w7-filtergroup-head">Direction</span>
          <Segmented
            label="Direction"
            value={values.dir ?? 'all'}
            onChange={(id) => set({ dir: id === 'all' ? null : (id as 'give' | 'take') })}
            items={[
              { id: 'all', label: 'All' },
              { id: 'give', label: 'Given' },
              { id: 'take', label: 'Taken' },
            ]}
          />
        </div>
      ) : null}

      {omission ? <p className="w7-filteromission">{omission}</p> : null}
    </>
  )

  return (
    <>
      <Button
        ref={anchor}
        icon="filter"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {active > 0 ? `Filters (${active})` : 'Filters'}
      </Button>

      {narrow ? (
        <Drawer open={open} onClose={() => setOpen(false)} title="Filters">
          {body}
        </Drawer>
      ) : (
        <Popover
          open={open}
          onClose={() => setOpen(false)}
          anchorRef={anchor}
          label="Filters"
          width={340}
        >
          {body}
        </Popover>
      )}
    </>
  )
}

/* ---------------------------------------------------------------- helpers -- */

/**
 * The text box.
 *
 * Local state with a 250ms commit, because the URL is the query: writing on
 * every keystroke would re-run the report six times for a six-letter word.
 * `replace: true` means no history entries either way — this is purely about
 * not asking the API a question the operator has not finished typing.
 */
function FilterText({
  value,
  onCommit,
  label,
  placeholder,
}: {
  value: string
  onCommit: (next: string) => void
  label: string
  placeholder: string
}) {
  const [text, setText] = useState(value)
  const committed = useRef(value)
  /* Held in a ref so the debounce depends on the TYPING and nothing else. The
     parent rebuilds this callback on every render, and a poll landing mid-word
     would otherwise restart the timer. */
  const commit = useRef(onCommit)
  commit.current = onCommit

  // The URL changing under us (Clear all, a pasted link, the back button) wins.
  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value
      setText(value)
    }
  }, [value])

  useEffect(() => {
    if (text === committed.current) return
    const id = setTimeout(() => {
      committed.current = text
      commit.current(text)
    }, 250)
    return () => clearTimeout(id)
  }, [text])

  return (
    <SearchInput value={text} onChange={setText} label={label} placeholder={placeholder} width={240} />
  )
}

/** Below 900px the collapsed filters open as a drawer — a 340px popover on a
 *  375px screen is a modal that has not admitted it. */
function useNarrow(query = '(max-width: 900px)') {
  // `window.matchMedia` is guarded, not assumed. The two other media hooks in
  // this app (DownlineTable, CreateAccountPage) already guard it; this one did
  // not, and threw outright wherever the API is missing — which is any
  // non-browser render. Absent means "not narrow", i.e. the popover branch.
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.(query).matches),
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia(query)
    const onChange = () => setNarrow(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return narrow
}

/* ------------------------------------------------------------ provenance -- */

/**
 * Non-dismissible on purpose. This is provenance, not a notification: a P&L
 * screenshot with no owner on it is how money arguments start.
 */
export function ScopeBanner({ account, onClear }: { account: Account; onClear: () => void }) {
  return (
    <div className="w7-scopebanner">
      <Banner
        tone="info"
        title={`You are reading this report as ${account.username}.`}
        actions={
          <Button variant="secondary" icon="user" onClick={onClear}>
            Back to your own view
          </Button>
        }
      >
        These figures are theirs, not yours — {ROLE_LABEL[account.role]}
        {account.displayName ? ` · ${account.displayName}` : ''}.
      </Banner>
    </div>
  )
}

/**
 * A pasted URL carrying a scope outside your subtree.
 *
 * Never a silent fall back to self: that would draw YOUR numbers under THEIR
 * name, which is the one failure mode a scoped report must not have.
 */
export function ScopeUnavailable({ onClear }: { onClear: () => void }) {
  return (
    <StateBlock
      icon="lock"
      tone="error"
      title="That account is not beneath you"
      actions={
        <Button variant="secondary" icon="user" onClick={onClear}>
          View your own
        </Button>
      }
    >
      This report can only be read as yourself or as an agent in your own downline. The link you
      followed names an account that is not one of them.
    </StateBlock>
  )
}

/** ` · Viewing as rk.master01 · Master`, for a page-head subtitle. */
export function scopeSuffix(account: Account | null): string {
  return account ? ` · Viewing as ${account.username} · ${ROLE_LABEL[account.role]}` : ''
}

/** `my-pnl-rk.master01-2026-07-20_2026-07-26.csv` — the file names its owner. */
export function scopedCsvName(base: string, account: Account | null, range: DateRange): string {
  return `${base}${account ? `-${account.username}` : ''}-${range.from}_${range.to}.csv`
}

/** The empty state for a table that is empty BECAUSE of the filters. */
export function FilteredEmpty({
  state,
  icon = 'search',
}: {
  state: ReportFilterState
  icon?: IconName
}) {
  return (
    <StateBlock
      icon={icon}
      title="No rows match these filters"
      actions={
        <Button variant="secondary" icon="close" onClick={state.clearAll}>
          Clear all filters
        </Button>
      }
    >
      {state.narrowest ? `${state.narrowest} ` : null}
      Widen or remove a filter to bring rows back — the chips above the table list everything that
      is currently narrowing it.
    </StateBlock>
  )
}

/** Agent tiers, for the report that filters by counterparty tier. */
export const AGENT_TIERS: Role[] = ROLES.filter(isAgent)
